/* eslint-disable monorepo/no-internal-import */
import React, {ComponentType} from 'react';
import {registerComponent} from './HydrationProvider';
import {useAddToResourceHints} from './ResourceHintsProvider';

const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 5000,
} as const;

/**
 * Tracks whether the page is currently unloading or backgrounded.
 *
 * Mobile browsers (Safari, in-app WebViews) cancel in-flight network requests when
 * the user navigates away or backgrounds the tab. Those cancellations surface as
 * dynamic-import rejections, but they are not real failures — there is no bug to
 * fix and no user to surface an error to. We use this signal to short-circuit
 * retries (which are guaranteed to also fail while the page is hidden) and to mark
 * the resulting error so telemetry can drop it.
 */
let pageNavigatingAway =
  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-module-scope
  process.browser && document.visibilityState === 'hidden';

if (process.browser) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      pageNavigatingAway = true;
    }
  });
  window.addEventListener('pagehide', () => {
    pageNavigatingAway = true;
  });
  window.addEventListener('pageshow', event => {
    // bfcache restore: the page is alive again, future imports should retry normally.
    if (event.persisted) {
      pageNavigatingAway = false;
    }
  });
}

/**
 * Marker thrown by the retry mechanism when a chunk load fails *while the user is
 * navigating away*. The `ErrorBoundary` reads this flag to suppress telemetry —
 * cancelled requests are noise, not bugs.
 */
export class NavigationCancelledChunkError extends Error {
  readonly isNavigationCancelled = true;
  constructor(cause: Error) {
    super(cause.message);
    this.name = 'NavigationCancelledChunkError';
    this.stack = cause.stack;
  }
}

/**
 * Checks if an error is related to dynamic import/chunk loading failure.
 * These errors can occur due to:
 * - Network issues (especially on mobile/iOS)
 * - Browser cache issues
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const errorPatterns = [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'error loading dynamically imported module',
    'Loading chunk',
    'Loading CSS chunk',
    'ChunkLoadError',
    'Failed to fetch',
    'Script error',
    'NetworkError',
    'TypeError: Load failed',
  ];

  return errorPatterns.some(
    pattern =>
      error.message?.includes(pattern) || error.name?.includes(pattern),
  );
}

function calculateRetryDelay(attempt: number): number {
  const exponentialDelay = RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt);
  const cappedDelay = Math.min(exponentialDelay, RETRY_CONFIG.maxDelayMs);
  // Add 0-30% random jitter to prevent synchronized retries
  const jitter = cappedDelay * Math.random() * 0.3;
  return cappedDelay + jitter;
}

/**
 * Waits for the specified duration.
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Creates a reliable importer that retries failed dynamic imports with:
 * - Exponential backoff
 * - Multiple retry attempts
 * - Detailed error logging
 */
function createReliableImporter<T extends ComponentType<any>>(
  importer: () => Promise<{default: T}>,
): () => Promise<{default: T}> {
  return async () => {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
      try {
        const module = await importer();
        return module;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        const isRetryable = isChunkLoadError(error);
        const hasMoreAttempts = attempt < RETRY_CONFIG.maxRetries;

        // The user navigated away (or backgrounded the tab) while the chunk was in
        // flight, and the browser cancelled the request. Retrying is futile —
        // mobile browsers refuse network during the unload/hidden window — and the
        // failure is not a real bug. Stop early and tag the error so the boundary
        // can drop it from telemetry.
        if (isRetryable && pageNavigatingAway) {
          throw new NavigationCancelledChunkError(lastError);
        }

        if (isRetryable && hasMoreAttempts) {
          const retryDelay = calculateRetryDelay(attempt);
          await delay(retryDelay);
        } else if (!isRetryable) {
          throw lastError;
        }
      }
    }

    throw lastError;
  };
}

export function lazy<T extends ComponentType<any>>(
  importer: () => Promise<{default: T}>,
  config: {
    /**
     * If true, the lazy-loaded module will be optimized for preloading as a priority resource.
     * Usually, this is only necessary for main Page/Route components.
     * Other modules should be downloaded lazily by react on demand when hydrating the page.
     */
    aboveTheFold?: boolean;
    /**
     * If true, the lazy-loaded module will be treated as an entry point that needs hydration.
     * The useHydrationDone hook will return false until the module has been hydrated on the client.
     * Set this flag to true for components that are Server-Side Rendered, for example, a SSRed Page component.
     *
     * PS: Do not set this to true for components that are only rendered on the client side (e.g. modals, dropdowns, components which are rendered on when inView etc)
     */
    isSsrAndNeedsHydration?: boolean;
  } & (
    | {
        /**
         * The fallback component to show while the lazy-loaded module is loading.
         * If not provided, the Suspense Boundary will render nothing until the module is loaded.
         */
        fallback?: NonNullable<React.ReactNode> | null;
      }
    | {
        /**
         * If true, the Suspense Boundary will be skipped and the module will still be loaded lazily but would not suspend the rendering.
         */
        skipSuspense?: true;
      }
  ) = {},
) {
  const {isSsrAndNeedsHydration, aboveTheFold} = config;

  // Wrap the importer in a reliable retry mechanism
  const reliableImporter = createReliableImporter(importer);

  const LazyLoaded = React.lazy(reliableImporter);

  const componentPath =
    importer.toString().match(/import\(['"](.+?)['"]\)/)?.[1] || '';
  const componentName = componentPath.split('/').pop() || '';
  const componentId = `${componentName}`;

  const Suspensified = ({ref, ...props}: {ref?: React.Ref<unknown>}) => {
    if (!process.browser && aboveTheFold) {
      // eslint-disable-next-line react-hooks/rules-of-hooks -- its okay break the rules here because the config.aboveTheFold is a constant, would never change for a given component
      useAddToResourceHints(importer.toString());
    }

    if (config && 'skipSuspense' in config && config.skipSuspense) {
      return <LazyLoaded {...(props as any)} ref={ref} />;
    }

    if (!process.browser) {
      return (
        <React.Suspense fallback={null}>
          <LazyLoaded {...(props as any)} ref={ref} />
        </React.Suspense>
      );
    }

    if (process.browser && isSsrAndNeedsHydration) {
      registerComponent(componentId);
    }

    return (
      <React.Suspense
        fallback={config && 'fallback' in config ? config.fallback : null}
      >
        {isSsrAndNeedsHydration && (
          <LazyMarkHydrationDone componentId={componentId} />
        )}

        <LazyLoaded {...(props as any)} ref={ref} />
      </React.Suspense>
    );
  };

  Suspensified.preload = importer;

  return Suspensified as unknown as T & {preload: () => Promise<void>};
}

/**
 * Lazy load this too to be like the main component, which is lazy loaded as well.
 */
const LazyMarkHydrationDone = React.lazy(() =>
  import('./MarkHydrationDone').then(mod => ({
    default: mod.MarkHydrationDone,
  })),
);
